import { Input as InputBase } from 'antd';
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { theme } from '../../theme/semanticTokens';
import { Typography } from '../../Typography';

const { TextArea: AntTextArea } = InputBase;

export type TextAreaProps = Partial<{
  value: string;
  placeholder: string;
  disabled: boolean;
  readOnly: boolean;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  className: string;
  id: string;
  name: string;
  rows: number;
  maxLength: number;
  status: 'error' | 'default';
  autoSize: boolean | { minRows?: number; maxRows?: number };
  helpText: string;
}>;

const StyledTextArea = styled(
  forwardRef<HTMLTextAreaElement, any>((props, ref) => (
    <AntTextArea {...props} ref={ref} />
  ))
)`
  &&.ant-input {
    border-radius: ${theme.dime.radius.m};
    border: 1px solid ${theme.dime.colors.border.static};
    padding: ${theme.dime.spacing.m} ${theme.dime.spacing.l};
    min-height: 40px;
    box-shadow: none;
  }

  &&.ant-input.error {
    border-color: ${theme.dime.colors.border.critical};
  }

  &&.ant-input:disabled {
    background-color: ${theme.dime.colors.fill.disabled};
    color: ${theme.dime.colors.text.disabled};
    border-color: ${theme.dime.colors.border.static};
    cursor: not-allowed;
  }

  &&.ant-input:disabled::placeholder {
    color: ${theme.dime.colors.text.disabled};
  }

  &&.ant-input[readonly] {
    background-color: ${theme.dime.colors.fill.disabled};
    color: ${theme.dime.colors.text.secondary};
    border-color: ${theme.dime.colors.border.static};
    cursor: default;
  }

  &&.ant-input:focus,
  &&.ant-input:focus-within {
    border-color: ${theme.dime.colors.border.interactiveSelected};
    box-shadow: none;
  }

  &&.ant-input.error:focus,
  &&.ant-input.error:focus-within {
    border-color: ${theme.dime.colors.border.critical};
  }
`;

const TextAreaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.dime.spacing.s};
`;

const HelpText = styled(Typography)<{ $hasError: boolean }>`
  color: ${({ $hasError }) =>
    $hasError
      ? theme.dime.colors.text.critical
      : theme.dime.colors.text.secondary};
`;

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      placeholder = '',
      disabled = false,
      readOnly = false,
      onChange,
      onFocus,
      onBlur,
      className = '',
      id,
      name,
      rows = 3,
      maxLength = 500,
      status = 'default',
      autoSize,
      helpText,
      ...rest
    },
    ref
  ) => {
    const combinedClassName = `${status} ${className}`;

    const hasError = status === 'error';

    return (
      <TextAreaWrapper>
        <StyledTextArea
          ref={ref}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          className={combinedClassName}
          id={id}
          name={name}
          rows={rows}
          maxLength={maxLength}
          status={status}
          autoSize={autoSize}
          {...rest}
        />
        {!!helpText && (
          <HelpText variant="body1" $hasError={hasError}>
            {helpText}
          </HelpText>
        )}
      </TextAreaWrapper>
    );
  }
);

TextArea.displayName = 'TextArea';
